/**
 * Shared normalization for an inbound `sellerId` before it reaches a uuid column.
 *
 * BUG-1003: both lot-create paths independently wrote `sellerId ?? null`, which
 * only replaces null/undefined. The forms send `''` for "no seller" (ItemForm
 * seeds `sellerId: ''` and re-sets `''` when the seller search is cleared), so
 * the empty string survived. Being falsy it also slipped past the
 * `if (resolvedSellerId)` tenant-scoped existence check, then hit
 * `seller_id` / `seller_id` — both uuid — as `''`,
 * which Postgres rejects with 22P02 (`invalid input syntax for type uuid`).
 * That error isn't mapped, so it surfaced as a generic 500.
 *
 * The bug reached two files because `inventory-lot-create-service.ts` was
 * written to "mirror the existing single-lot route" and copied the shape along
 * with the defect. This helper is the single seam so a third copy can't drift.
 *
 * BL-1433 moved it here from `apps/admin-web/src/lib/customers/` so the Zod rule
 * in `schemas/item-form.ts` shares the SAME definition of "empty" as the routes.
 * A package cannot import from an app, so leaving it app-side would have meant
 * the schema re-implementing the trim check — reintroducing exactly the
 * duplication BUG-1003 closed.
 */

/**
 * Coerce an inbound seller id into either a non-empty trimmed string or `null`.
 *
 * Accepts `unknown` so it can be called both on a loosely-typed request body
 * and on an already-validated `string | null | undefined` service input.
 * Anything that isn't a non-empty string — `''`, whitespace-only, `null`,
 * `undefined`, or a non-string value — becomes `null`.
 *
 * @param raw  the caller-supplied seller id, from a request body or service input
 * @returns the trimmed id, or `null` when no usable id was supplied
 */
export function normalizeSellerId(raw: unknown): string | null {
  return typeof raw === 'string' ? raw.trim() || null : null;
}
